/**
 * Storage clearing utilities for PWA and browser storage
 */

// Silent version for use with custom UI dialogs
export const clearAllStorageAndCachesSilent = async (): Promise<void> => {
  try {
    const clearedItems: string[] = [];

    // 1. Clear traditional browser storage
    window.localStorage.clear();
    window.sessionStorage.clear();
    clearedItems.push('✅ localStorage & sessionStorage');

    // 2. Clear IndexedDB (major PWA storage mechanism)
    if ('indexedDB' in window) {
      try {
        // Get all databases and delete them
        if ('databases' in indexedDB) {
          const databases = await (
            indexedDB as unknown as {
              databases: () => Promise<{ name?: string }[]>;
            }
          ).databases();
          await Promise.all(
            databases.map(async (db: { name?: string }) => {
              if (db.name) {
                const deleteReq = (
                  indexedDB as unknown as {
                    deleteDatabase: (name: string) => IDBOpenDBRequest;
                  }
                ).deleteDatabase(db.name);
                return new Promise((resolve, reject) => {
                  deleteReq.onsuccess = () => resolve(void 0);
                  deleteReq.onerror = () => reject(deleteReq.error);
                });
              }
            })
          );
          clearedItems.push(`✅ IndexedDB (${databases.length} databases)`);
        } else {
          // Fallback: try to clear common database names
          const commonDbNames = [
            'workbox-precache',
            'workbox-runtime',
            'keyval-store',
            'app-data',
          ];
          for (const dbName of commonDbNames) {
            try {
              // @ts-expect-error - deleteDatabase exists but TypeScript doesn't recognize it
              const deleteReq = indexedDB.deleteDatabase(dbName);
              await new Promise((resolve) => {
                deleteReq.onsuccess = deleteReq.onerror = () => resolve(void 0);
              });
            } catch {
              // Ignore errors for non-existent databases
            }
          }
          clearedItems.push('✅ IndexedDB (fallback clearing)');
        }
      } catch {
        clearedItems.push('⚠️ IndexedDB (partial clear)');
      }
    }

    // 3. Clear WebSQL (deprecated but might exist)
    if ('openDatabase' in window) {
      try {
        // WebSQL is deprecated but might exist
        const db = (
          window as unknown as {
            openDatabase: (
              a: string,
              b: string,
              c: string,
              d: string
            ) => unknown;
          }
        ).openDatabase('', '', '', '');
        if (db) {
          clearedItems.push('✅ WebSQL');
        }
      } catch {
        // WebSQL not supported or already clear
      }
    }

    // 4. Clear Service Worker Caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
      clearedItems.push(
        `✅ Service Worker Caches (${cacheNames.length} caches)`
      );
    }

    // 5. Clear Service Workers + Push Subscriptions + Background Sync
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();

      for (const registration of registrations) {
        // Clear push subscriptions
        if (
          'pushManager' in registration &&
          'getSubscription' in registration.pushManager
        ) {
          try {
            const subscription =
              await registration.pushManager.getSubscription();
            if (subscription) {
              await subscription.unsubscribe();
            }
          } catch {
            // Ignore push subscription errors
          }
        }

        // Clear background sync (if supported)
        if (
          'sync' in registration &&
          'getTags' in
            (
              registration as unknown as {
                sync: { getTags: () => Promise<string[]> };
              }
            ).sync
        ) {
          try {
            // Background Sync API is experimental
            const tags = await (
              registration as unknown as {
                sync: { getTags: () => Promise<string[]> };
              }
            ).sync.getTags();
            for (const tag of tags) {
              try {
                await (
                  registration as unknown as {
                    sync: { unregister: (tag: string) => Promise<void> };
                  }
                ).sync.unregister(tag);
              } catch {
                // Ignore sync unregister errors
              }
            }
          } catch {
            // Background sync not supported
          }
        }

        // Unregister the service worker
        await registration.unregister();
      }

      clearedItems.push(
        `✅ Service Workers + Push + Background Sync (${registrations.length} workers)`
      );
    }

    // 6. Enhanced Cookie Clearing (all domains and paths)
    const cookiesList = document.cookie.split(';');
    let cookiesCleared = 0;

    for (const cookie of cookiesList) {
      if (!cookie || cookie.trim() === '') continue;

      const firstEqual = cookie.indexOf('=');
      if (firstEqual <= 0) continue;

      const cookieName = cookie.substring(0, firstEqual).trim();
      const cookieBase = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`;

      // Clear with multiple domain and path variations
      const clearOptions = [
        `${cookieBase}; path=/`,
        `${cookieBase}; path=/; domain=${window.location.hostname}`,
        `${cookieBase}; path=/; domain=.${window.location.hostname}`,
      ];

      // Add variations for parent domains
      const domainParts = window.location.hostname.split('.');
      if (domainParts.length > 1) {
        for (let i = 1; i < domainParts.length; i++) {
          const parentDomain = domainParts.slice(i).join('.');
          clearOptions.push(`${cookieBase}; path=/; domain=${parentDomain}`);
          clearOptions.push(`${cookieBase}; path=/; domain=.${parentDomain}`);
        }
      }

      // Apply all clearing options
      clearOptions.forEach((option) => {
        document.cookie = option;
      });

      cookiesCleared++;
    }

    clearedItems.push(
      `✅ Cookies (${cookiesCleared} cookies, multiple domains)`
    );

    // 7. Clear Application Cache (deprecated but might exist)
    if ('applicationCache' in window && window.applicationCache) {
      try {
        // Application Cache is deprecated
        (window.applicationCache as { update: () => void }).update();
        clearedItems.push('✅ Application Cache');
      } catch {
        // Application cache not supported or already clear
      }
    }

    // 8. Force garbage collection if available
    if (
      'gc' in window &&
      typeof (window as unknown as { gc?: () => void }).gc === 'function'
    ) {
      try {
        // gc is not standard
        (window as unknown as { gc: () => void }).gc();
        clearedItems.push('✅ Garbage Collection');
      } catch {
        // Garbage collection not available
      }
    }

    // Log comprehensive results (no browser alerts)
    console.log('🧹 COMPREHENSIVE STORAGE RESET COMPLETE!', clearedItems);

    // Reload after a short delay to ensure all operations complete
    setTimeout(() => {
      window.location.reload();
    }, 500);
  } catch (e) {
    console.error(`Error during comprehensive reset: ${e}`);
    // Still reload on error to ensure fresh state
    window.location.reload();
  }
};

// Original version with browser dialogs for debug purposes
export const clearAllStorageAndCaches = async (): Promise<void> => {
  if (
    !confirm(
      '🚨 RESET: This will clear ALL storage, caches, service workers, and reload the page. Continue?'
    )
  ) {
    return;
  }

  try {
    await clearAllStorageAndCachesSilent();

    // Show comprehensive results
    const message = `🧹 COMPREHENSIVE STORAGE RESET COMPLETE!\n\nReloading page...`;
    alert(message);
  } catch (e) {
    alert(`Error during comprehensive reset: ${e}`);
    // Still reload on error to ensure fresh state
    window.location.reload();
  }
};
